import React from 'react'

const Cp_DMA = () => {
    return (
        <div className='container'>
            <h1>Dynamic Memory Allocation</h1>
            <h2>Allocating memory</h2>
        <p>
There are two ways that memory gets allocated for data storage:
</p>
<ol>
<li>Compile Time (or static) Allocation
 <ul>
 <li>Memory for named variables is allocated by the compiler</li>
 <li>Exact size and type of storage must be known at compile time</li>
 <li>For standard array declarations, this is why the size has to be 
     constant</li>
 </ul>
</li>

<li>Dynamic Memory Allocation
 <ul>
 <li>Memory allocated "on the fly" during run time</li>
 <li>dynamically allocated space usually placed in a program segment known 
     as the <i>heap</i> or the <i>free store</i></li>
 <li>Exact amount of space or number of items does not have to be known
     by the compiler in advance.</li>
 <li>For dynamic memory allocation, pointers are crucial</li>
 </ul>
</li>
</ol>
<h2>Dynamic Memory Allocation</h2>
<ul>
<li>We can dynamically allocate storage space while the program is 
running, but we cannot create new variable names "on the fly"</li>
<li>For this reason, dynamic allocation requires two steps:
 <ol>
 <li>Creating the dynamic space.</li>
 <li>Storing its <b>address</b> in a <b>pointer</b> (so that the 
     space can be accesed)</li>
 </ol>
</li>

<li>To dynamically allocate memory in C++, we use the <b>new</b> 
operator.</li>

<li>De-allocation:
 <ul>
 <li>Deallocation is the "clean-up" of space being used for variables or 
     other data storage</li>
 <li>Compile time variables are automatically deallocated based on their 
     known extent (this is the same as scope for "automatic" 
     variables)</li>
 <li>It is the programmer's job to deallocate dynamically created 
     space</li>
 <li>To de-allocate dynamic memory, we use the <b><tt>delete</tt></b> 
     operator</li>
 </ul>
</li>
</ul>
<h3>Allocating space with <tt>new</tt></h3>
<ul>
<li>To allocate space dynamically, use the unary operator 
<b><tt>new</tt></b>, followed by the <i>type</i> being allocated.
<div className='output'>
<pre> new int;        // dynamically allocates an int <br />
 new double;     // dynamically allocates a double
</pre></div>
</li>

<li>If creating an array dynamically, use the same form, but put brackets 
with a size after the type:
<div className='output'>
<pre> new int[40];      // dynamically allocates an array of 40 ints <br />
 new double[size]; // dynamically allocates an array of size doubles <br />
                   //  note that the size can be a variable <br />
</pre></div>
</li>

<li>These statements above are not very useful by themselves, because the
allocated spaces have no names! BUT, the <tt>new</tt> operator returns 
the starting address of the allocated space, and this address can be 
stored in a pointer:
<pre className='output'> int * p;        // declare a pointer p <br />
 p = new int;   <code className='comment'> // dynamically allocate an int and load address into p</code> <br /><br />

<code className='keywords'> double</code> * d;    <code className='comment'> // declare a pointer d</code> <br />
 d = new double;<code className='comment'> // dynamically allocate a double and load address into d</code> <br /><br />

 <code className='comment'>// we can also do these in single line statements</code> <br />
 int x = 40;<br />
 int * list = new int[x];<br />
 float * numbers = new float[x+10];<br />
</pre>
Notice that this is one more way of <i>initializing</i> a pointer to a 
valid target (and the most important one).<br/>
</li>
</ul>
<h3>Accessing dynamically created space</h3>
<ul>
<li>So once the space has been dynamically allocated, how do we use 
it?</li>

<li>For single items, we go through the pointer.  Dereference the pointer 
to reach the dynamically created target:
<pre className='output'>  int * p = new int;<code className='comment'>	// dynamic integer, pointed to by p</code> <br />
<br />
  *p = 10;	<code className='comment'>	// assigns 10 to the dynamic integer</code> <br />
  cout &lt;&lt; *p;	<code className='comment'>	// prints 10</code> <br />
</pre>
</li>

<li>For dynamically created arrays, you can use either pointer-offset 
notation, or treat the pointer as the array name and use the standard 
bracket notation:
<pre className='output'>  double * numList = new double[size];<code className='comment'>// dynamic array</code> <br />
<br />
  for (int i = 0; i &lt; size; i++) <br />
      numList[i] = 0;		<code className='comment'>	// initialize array elements to 0</code> <br />
<br />
  numList[5] = 20;		<code className='comment'>// bracket notation</code>	 <br />
  *(numList + 7) = 15;		<code className='comment'>	// pointer-offset notation</code> <br />
  <code className='comment'>//   means same as numList[7] </code> <br />
</pre>
</li>

</ul>
<h3>Deallocation of dynamic memory</h3>
<ul>
<li>To deallocate memory that was created with <tt>new</tt>, we use the 
unary operator <b><tt>delete</tt></b>. The one operand should be a pointer 
that stores the address of the space to be deallocated:
<pre className='containers'>  int * ptr = new int;		// dynamically created int <br />
  // ... <br />
  delete ptr;			// deletes the space that ptr points to <br />

</pre>
Note that the pointer <tt>ptr</tt> <i>still exists</i> in this example. 
That's a named variable subject to scope and extent determined at compile 
time.  It can be reused:
<pre className='output'>  ptr = new int[10];		// point p to a brand new array
</pre>
</li>

<li>To deallocate a dynamic array, use this form:
<pre className='output'>  delete [] <i>name_of_pointer</i>;
</pre>
Example:
<pre className='output'>  int * list = new int[40];	// dynamic array <br />
<br />
  delete [] list;		// deallocates the array <br />
  list = 0; 			// reset list to null pointer <br />
</pre>
After deallocating space, it's always a good idea to reset the pointer to 
null unless you are pointing it at another valid target right 
away.<br/>&nbsp;
</li>

<li><b>To consider:</b>  So what happens if you fail to deallocate dynamic 
memory when you are finished with it?  (i.e. why is deallocation 
important?)</li>

</ul>
<h2>
Application Example:  Dynamically resizing an array</h2>
<p>
If you have an existing array, and you want to make it bigger (add array
cells to it), you cannot simply append new cells to the old ones.&nbsp;
Remember that arrays are stored in consecutive memory, and you never know
whether or not the memory immediately after the array is already allocated 
for something else. &nbsp; For that reason, the process takes a few more 
steps.&nbsp; Here is an example using an integer array.&nbsp; Let's say 
this is the original array:

</p>
<pre className='output'> int * list = new int[size]; 
</pre>
<p>I want to resize this so that the array called <b>list</b> has space
for 5 more numbers (presumably because the old one is full).
<br/>There are four main steps.

</p>
<ol>
<li>Create an entirely new array of the appropriate type and of
the new size. (You'll need another pointer for this).
<pre className='output'> int * temp = new int[size + 5]; 
</pre>
</li>

<li>Copy the data from the old array into the new array (keeping
them in the same positions). This is easy with a for-loop.
<pre className='output'> for (int i = 0; i &lt; size; i++) <br />
    temp[i] = list[i]; 
</pre>
</li>

<li>Delete the old array -- you don't need it anymore! (Do
as your Mom says, and take out the garbage!)
<pre className='output'> delete [] list;  // this deletes the array pointed to by "list" </pre> 
</li>

<li>Change the pointer. You still want the array to be called 
"list" (its original name), so change the list pointer to the new address. 
<pre className='output'> list = temp; 
</pre>
</li>

</ol>
        </div>
    )
}

export default Cp_DMA
